import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { SchemaCampaign } from '@/__generated/es-api'
import { TEMPLATES_TO_TYPE } from '@/app/module/campaigns/definitions'
import { BackendCampaignTemplate, CampaignQuery, TCampaignListItem } from '@/app/module/campaigns/types'
import { selectUserTimezone } from '@/app/module/user/store/selectors'
import { formatDateTime } from '@/app/service/util'

import { useGetCampaigns } from './use-get-campaigns'

const EMPTY_DATE = '0001-01-01T00:00:00Z'

const toBoolean = (value?: string): boolean | undefined => {
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

export const useGetCampaignsList = (query: CampaignQuery) => {
  const parsedQuery = useMemo(() => {
    const { page, size, templates, search, archived, aborted, open, published, launched } = query

    return {
      page,
      size,
      templates,
      search,
      archived: toBoolean(archived),
      aborted: toBoolean(aborted),
      open: toBoolean(open),
      published: toBoolean(published),
      launched: toBoolean(launched),
    }
  }, [query])

  const result = useGetCampaigns(parsedQuery)
  const tz = useSelector(selectUserTimezone)

  const { data, ordered } = useMemo(() => {
    if (!result.data?.engagements) {
      return { data: {}, ordered: [] }
    }

    const transformedList = result.data.engagements.map((item) => transformCampaignListItem(item, tz))

    const dataMap = transformedList.reduce<Record<number, TCampaignListItem>>(
      (acc, item) => ({ ...acc, [item.id]: item }),
      {},
    )
    const orderedList = transformedList.map((item) => item.id)

    return { data: dataMap, ordered: orderedList }
  }, [result.data, tz])

  return {
    ...result,
    data,
    ordered,
    total: result.data?.total || 0,
  }
}

export const transformCampaignListItem = (
  {
    id,
    name,
    template,
    autoLaunchAt,
    createdAt,
    launched,
    launchedAt,
    published,
    aborted,
    open,
    archived,
    launchOpen,
    runs = 0,
    runsDone = 0,
    runsError = 0,
    runsInProgress = 0,
    runsUnsubbed = 0,
    runsWithFailures = 0,
  }: SchemaCampaign,
  tz: string,
): TCampaignListItem => {
  let launchedData = 'Draft'
  if (launched) {
    launchedData = formatDateTime(launchedAt!, tz)
  } else if (autoLaunchAt && autoLaunchAt !== EMPTY_DATE) {
    launchedData = formatDateTime(autoLaunchAt, tz)
  }
  const subscriptionOpen = launched ? open : launchOpen

  return {
    id,
    name,
    type: TEMPLATES_TO_TYPE[template as BackendCampaignTemplate] || template,
    createdAt,
    launched: launchedData,
    state: {
      launched,
      open,
      published,
      aborted,
      archived,
    },
    open: subscriptionOpen,
    runs: {
      started: runs,
      launched,
      open: subscriptionOpen,
      published,

      inProgress: runs - runsDone,
      live: runsInProgress,
      scheduled: runs - runsDone - runsInProgress,

      completed: runsDone,
      endpointReached: runsDone - runsError - runsUnsubbed,
      unsubscribed: runsUnsubbed,

      error: runsError,
      runsWithFailures,
    },
  }
}
